import React, { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import {
    GetUserFavoritesQueryVariables,
    useAddFavoritePlayerForUserMutation,
    useGetFavoritePlayerIdsQuery,
    useGetUserFavoritesQuery,
    useRemoveFavoritePlayerForUserMutation,
} from '@/api/lobby/query.generated';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { generateLobbyPlayerCards } from '@/feature/lobby/utils/lobbyPlayers';
import { useUserStore } from '@/hooks/use-user-store';
import { UseQueryArgs } from 'urql';

import { PlayerWithTeamAndEvent } from '../types';

type FavoritesTypes = {
    favoritesIds: string[];
    favoritePlayers: PlayerWithTeamAndEvent[];
    fetching: boolean;
    hasNoData: boolean;
    addFavorite: (playerId: string) => void;
    removeFavorite: (playerId: string) => void;
};

const FavoritesContext = React.createContext<FavoritesTypes>({
    favoritesIds: [],
    favoritePlayers: [],
    fetching: false,
    hasNoData: false,
    addFavorite: () => {},
    removeFavorite: () => {},
});

export const FavoritesProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const profile = useUserStore(state => state.profile);
    const guest = useUserStore(state => state.guest);
    const session = useUserStore(state => state.session);
    const requestPolicy = useGQLRequestPolicy();
    const { favoritePlayers, fetching, hasNoData, execute } = useFavoritesPlayers({
        requestPolicy,
        pause: true,
    });

    const [favoritesIds, setFavoritesIds] = React.useState<string[]>([]);

    const [{ data }] = useGetFavoritePlayerIdsQuery({
        variables: { userId: profile.sub },
        pause: guest || !session?.access_token,
    });

    useEffect(() => {
        if (data) {
            setFavoritesIds(data.getFavoritePlayerIds);
        }
    }, [data]);

    const [{}, addFavouritePlayer] = useAddFavoritePlayerForUserMutation();
    const [{}, removeFavouritePlayer] = useRemoveFavoritePlayerForUserMutation();

    const addFavorite = useCallback(
        async (playerId: string) => {
            const addFavouritePlayerResponse = await addFavouritePlayer({
                userId: profile.sub,
                playerId: playerId,
            });
            if (addFavouritePlayerResponse.data) {
                setFavoritesIds(addFavouritePlayerResponse.data.addFavoritePlayerForUser);
                execute();
            }
        },
        [addFavouritePlayer, execute, profile.sub]
    );

    const removeFavorite = useCallback(
        async (playerId: string) => {
            const removeFavoriteResponse = await removeFavouritePlayer({
                userId: profile.sub,
                playerId: playerId,
            });
            if (removeFavoriteResponse.data) {
                setFavoritesIds(removeFavoriteResponse.data.removeFavoritePlayerForUser);
                execute();
            }
        },
        [execute, removeFavouritePlayer, profile.sub]
    );

    const value = useMemo(
        () => ({ favoritesIds, favoritePlayers, hasNoData, fetching, addFavorite, removeFavorite }),
        [favoritesIds, favoritePlayers, hasNoData, fetching, addFavorite, removeFavorite]
    );

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

/**
 * Hook returning the favorite players, as returned by 'getUpcomingFavoritePlayerEventsV2' query
 */
const useFavoritesPlayers = (options: Omit<UseQueryArgs<GetUserFavoritesQueryVariables>, 'query' | 'variables'>) => {
    const profile = useUserStore(state => state.profile);
    const isGuest = useUserStore(state => state.guest);
    const session = useUserStore(state => state.session);
    const userId = profile.sub; // userId is required for the favorites query
    const [{ data, fetching }, execute] = useGetUserFavoritesQuery({
        ...options,
        variables: { userId },
        pause: isGuest || !session?.access_token,
    });

    const favoritePlayers = useMemo(
        () => generateLobbyPlayerCards(data?.getUpcomingFavoritePlayerEventsV2 ?? []),
        [data?.getUpcomingFavoritePlayerEventsV2]
    );

    return {
        favoritePlayers,
        fetching,
        hasNoData: !data || favoritePlayers.length === 0,
        execute,
    };
};

export const useFavorites = () => {
    return React.useContext(FavoritesContext);
};
